import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import type { MyReferrals, PixKeyIn, ReferralPayout } from '@/api/types';

const MINE = ['referrals'] as const;

export function useMyReferrals() {
  return useQuery({ queryKey: MINE, queryFn: () => api.get<MyReferrals>('/me/referrals') });
}

export function useSavePixKey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PixKeyIn) => api.put<MyReferrals>('/me/referrals/pix-key', body),
    onSuccess: (data) => client.setQueryData(MINE, data),
  });
}

// ── Console ──────────────────────────────────────────────────────────────────

export type PayoutFilter = 'due' | 'processing' | 'failed' | 'paid' | 'all';
const ADMIN = ['admin', 'referrals'] as const;

export function useReferralPayouts(filter: PayoutFilter) {
  return useQuery({
    queryKey: [...ADMIN, filter],
    queryFn: () =>
      api.get<ReferralPayout[]>(
        filter === 'all' ? '/admin/referrals' : `/admin/referrals?status=${filter}`,
      ),
  });
}

export function usePayoutAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      note,
    }: {
      id: string;
      action: 'pay' | 'mark-paid' | 'cancel';
      note?: string;
    }) =>
      api.post<ReferralPayout[]>(
        `/admin/referrals/${id}/${action}`,
        action === 'pay' ? undefined : { note: note || null },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: ADMIN }),
  });
}

/** Types the coupon of whoever referred me; the discount shows on Plus and the plan too. */
export function useUseCoupon() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post<MyReferrals>('/me/referrals/coupon', { code }),
    onSuccess: (data) => {
      client.setQueryData(MINE, data);
      void client.invalidateQueries({ queryKey: ['plus'] });
      void client.invalidateQueries({ queryKey: ['tenant'] });
    },
  });
}
